<template>
  <e-form v-model="isValid" ref="formRef" class="d-flex flex-column row-gap-4" @submit="submit">
    <div class="h3">Настройка подключения к менеджеру каналов</div>
    <div class="paragraph-s_h mb-10 text-secondary">
      Укажите наименование для подключения (оно будет отображаться в списке подключений). При необходимости
      откорректируйте настройки.
    </div>
    <multilanguage-field
      v-model="data.name"
      :rules="[required()]"
      :common-rules="[maxLength(255)]"
      label="Наименование подключения"
    />
    <e-select
      v-model="data.rules"
      :items="penaltySchemeCollection"
      :rules="[required()]"
      item-title="name"
      item-value="shortName"
      label="Правила штрафования"
    />
    <e-select
      v-model="data.payment"
      :items="supplierPayRulesCollection"
      :rules="[required()]"
      item-title="name"
      item-value="shortName"
      label="Оплата поставщику"
    />
    <div class="d-flex flex-column flex-md-row col-gap-4 row-gap-4">
      <e-time-picker
        v-model="data.time[0]"
        :rules="[required()]"
        :error-messages="checkInErrorMessage"
        label="Расчётный час заезда"
      />
      <e-time-picker
        v-model="data.time[1]"
        :rules="[required()]"
        :error-messages="checkOutErrorMessage"
        label="Расчётный час выезда"
      />
    </div>
    <div class="d-flex align-center">
      <e-checkbox v-model="data.checkOutOnNext" label="Выезд на следующие сутки" />
      <help-tooltip class="ml-2"
        >Когда установлена галочка "Выезд на следующие сутки", выезд происходит на следующие сутки. При снятой галочке
        заезд и выезд происходят в одни сутки.
      </help-tooltip>
    </div>

    <teleport v-if="isMounted" defer :to="`#${travelLineIntegrationSubmitButtonTeleport}`">
      <e-btn append-icon-src="arrowRight" :loading="isLoading" variant="primary" @click="submit"> Далее </e-btn>
    </teleport>
  </e-form>
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core';
import { ref, toRef } from 'vue';
import { EBtn, ECheckbox, EForm, ESelect, ETimePicker, HelpTooltip, MultilanguageField } from '@/core/components/ui';
import { travelLineIntegrationSubmitButtonTeleport } from '@/core/consts/teleport';
import { useCollectionsStore } from '@/core/store/collections';
import { getTimeFromPTH } from '@/core/utils/date';
import { getMultiLanguagesValues } from '@/core/utils/languages';
import { isBoolean } from '@/core/utils/type-guards';
import { maxLength, required } from '@/core/utils/validation';
import { useTravelLineStore } from '@/parts/tariffs/store/travelLine';

const emit = defineEmits<{
  (event: 'next'): void;
}>();

const { updateIntegrationSettings, integration } = useTravelLineStore();

const {
  penaltySchemeCollection,
  getPenaltySchemeCollection,
  supplierPayRulesCollection,
  getSupplierPayRulesCollection,
} = useCollectionsStore();

const isMounted = useMounted();

const integrationSetting = toRef(() => integration.value?.master?.steps.integrationSettings);

const data = ref({
  name: integrationSetting.value?.connectionName
    ? getMultiLanguagesValues(integrationSetting.value?.connectionName)
    : null,
  rules: integrationSetting.value?.penaltySchemeCode || null,
  payment: integrationSetting.value?.supplierPayRuleCode || null,
  time: [
    integrationSetting.value?.checkInOutSettings?.checkInTime || 'PT14H00M',
    integrationSetting.value?.checkInOutSettings?.checkOutTime || 'PT12H00M',
  ],
  checkOutOnNext: isBoolean(integrationSetting.value?.checkInOutSettings?.nextDayCheckout)
    ? integrationSetting.value?.checkInOutSettings?.nextDayCheckout
    : true,
});

const isLoading = ref(false);
const isValid = ref(false);

const checkInErrorMessage = toRef(() => {
  if (!data.value.time[0] || !data.value.time[1]) {
    return '';
  }

  return !data.value.checkOutOnNext && getTimeFromPTH(data.value.time[1]) === getTimeFromPTH(data.value.time[0])
    ? 'Значения не могут совпадать'
    : !data.value.checkOutOnNext && getTimeFromPTH(data.value.time[0]) > getTimeFromPTH(data.value.time[1])
      ? 'Заезд не может быть позже выезда'
      : '';
});
const checkOutErrorMessage = toRef(() => {
  if (!data.value.time[0] || !data.value.time[1]) {
    return '';
  }
  
  return !data.value.checkOutOnNext && getTimeFromPTH(data.value.time[1]) === getTimeFromPTH(data.value.time[0])
    ? 'Значения не могут совпадать'
    : '';
});

const formRef = ref();

const submit = async () => {
  formRef.value?.validate();

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const result = await updateIntegrationSettings({
    connectionName: data.value.name!,
    penaltySchemeCode: data.value.rules!,
    supplierPayRuleCode: data.value.payment!,
    checkInOutSettings: {
      checkInTime: data.value.time[0]!,
      checkOutTime: data.value.time[1]!,
      nextDayCheckout: data.value.checkOutOnNext,
    },
  });

  isLoading.value = false;

  if (!result?.errors) {
    emit('next');
  }
};

const promises = [];
promises.push(getSupplierPayRulesCollection());
promises.push(getPenaltySchemeCollection());

await Promise.all(promises);

if (!data.value.rules && penaltySchemeCollection.value?.length === 1) {
  data.value.rules = penaltySchemeCollection.value[0].shortName;
}

if (!data.value.payment && supplierPayRulesCollection.value?.length === 1) {
  data.value.payment = supplierPayRulesCollection.value[0].shortName;
}
</script>

<style scoped lang="scss"></style>
